import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import useScrollLock from '../../hooks/useScrollLock';
import { getImageUrl, downloadFileFromUrl, sanitizeDownloadFilename } from '../../utils/api';
import './styles/catalogues.css';

const BrandCataloguesViewerModal = ({ isOpen, onClose, salonId, brand }) => {
  const { t } = useLanguage();
  const [catalogues, setCatalogues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewingCatalogue, setViewingCatalogue] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useScrollLock(isOpen);

  const fetchCatalogues = useCallback(async () => {
    if (!salonId || !brand?.id) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/salons/${salonId}/dress-brands/${brand.id}/catalogues`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCatalogues(response.data.catalogues || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || t('brands.failedToLoadCatalogues'));
      setCatalogues([]);
    } finally {
      setLoading(false);
    }
  }, [salonId, brand?.id, t]);

  useEffect(() => {
    if (isOpen && brand?.id) {
      fetchCatalogues();
    } else {
      setCatalogues([]);
      setViewingCatalogue(null);
      setError('');
    }
  }, [isOpen, brand?.id, fetchCatalogues]);

  const handleClose = () => {
    setViewingCatalogue(null);
    onClose();
  };

  if (!isOpen || !brand) return null;

  const pdfUrl = viewingCatalogue ? getImageUrl(viewingCatalogue.pdf_path) : null;

  const handleDownload = async () => {
    if (!viewingCatalogue || !pdfUrl || downloading) return;
    setDownloading(true);
    try {
      const filename = sanitizeDownloadFilename(
        `${viewingCatalogue.year}-${viewingCatalogue.name}`
      );
      await downloadFileFromUrl(pdfUrl, filename);
    } catch {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="modal-overlay">
        <div className="modal catalogue-list-modal">
          <div className="modal-header">
            <h3>{brand.brand_name} — {t('brands.catalogues')}</h3>
            <button type="button" className="modal-close" onClick={handleClose}>×</button>
          </div>

          <div className="modal-body">
            {loading && <p>{t('common.loading')}</p>}
            {error && <div className="error-message">{error}</div>}

            {!loading && !error && catalogues.length === 0 && (
              <p className="catalogues-empty">{t('brands.noCatalogues')}</p>
            )}

            {!loading && catalogues.length > 0 && (
              <ul className="catalogues-list catalogue-picker-list">
                {catalogues.map((catalogue) => (
                  <li key={catalogue.id}>
                    <button
                      type="button"
                      className="catalogue-list-item catalogue-list-button"
                      onClick={() => setViewingCatalogue(catalogue)}
                    >
                      <span className="catalogue-year">{catalogue.year}</span>
                      <span className="catalogue-name">{catalogue.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="salon-button secondary" onClick={handleClose}>
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>

      {viewingCatalogue && pdfUrl && (
        <div className="catalogue-pdf-fullscreen">
          <div className="modal-header catalogue-pdf-fullscreen-header">
            <div className="catalogue-pdf-fullscreen-actions">
              <button
                type="button"
                className="sleek-button change-log"
                onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
              >
                {t('brands.openInNewTab')}
              </button>
              <button
                type="button"
                className="sleek-button change-log"
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? `${t('common.loading')}...` : t('brands.downloadPdf')}
              </button>
              <button
                type="button"
                className="sleek-button change-log"
                onClick={() => setViewingCatalogue(null)}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
          <iframe
            src={pdfUrl}
            className="catalogue-pdf-fullscreen-viewer"
            title={viewingCatalogue.name}
          />
        </div>
      )}
    </>
  );
};

export default BrandCataloguesViewerModal;
